/**
 * UserTransactionDetailPage
 * UC-050: View Personal Transaction History - Transaction Detail
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useApi } from '../hooks/useApi';
import { TransactionResponse, ReceiptResponse } from '../types';
import { Button, Input, Card } from '../components/ui';
import { fmt, fmtMoney } from '../utils/format';

export function UserTransactionDetailPage() {
  const navigate = useNavigate();
  const { transactionId } = useParams<{ transactionId: string }>();
  const { t } = useTranslation();

  // Error state for external service unavailable
  const [showErrorState, setShowErrorState] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // API call to get transaction details
  const {
    data: transaction,
    loading: transactionLoading,
    error: transactionError
  } = useApi<TransactionResponse>(
    `/transactions/${transactionId}`,
    {
      onError: () => {
        toast.error(t('userTransactionDetail.errors.loadFailed', 'Failed to load transaction details'));
      }
    }
  );

  // API call to get receipt data
  const {
    data: receiptData,
    loading: receiptLoading,
    error: receiptError,
    execute: fetchReceipt
  } = useApi<ReceiptResponse>(
    `/transactions/${transactionId}/receipt`,
    {
      immediate: false, // Don't auto-fetch
      onError: (error) => {
        setShowErrorState(true);
        setErrorMessage(
          error.message ||
            t(
              'userTransactionDetail.errors.stripeUnavailable',
              'Unable to retrieve complete transaction details from payment service. Showing cached data.',
            ),
        );
      }
    }
  );

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAmount = (amount: number): string => {
    return fmtMoney(amount, 2);
  };

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'refunded':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Stripe hosts a polished receipt page per charge (and per subscription
  // invoice). When the page first loads we fetch that URL lazily; clicking
  // the button opens it in a new tab so the user can save it as PDF via
  // Stripe's built-in download. If Stripe hasn't produced one (pending /
  // archived) receiptData.receiptUrl is null and we tell the user.
  const openStripeReceipt = async (): Promise<void> => {
    if (!transaction) return;
    try {
      // Reuse already-fetched data if present, otherwise load it now.
      const data: ReceiptResponse | null =
        receiptData ?? (await fetchReceipt());
      const url = data?.receiptUrl;
      if (!url) {
        toast.error(
          t(
            'userTransactionDetail.toasts.receiptUnavailable',
            'Stripe receipt is not available for this transaction yet.',
          ),
        );
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(
        t('userTransactionDetail.toasts.receiptFailed', 'Could not open receipt'),
      );
    }
  };

  if (transactionLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transaction details...</p>
        </div>
      </div>
    );
  }

  if (transactionError || !transaction) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Transaction not found</h3>
          <p className="mt-2 text-base sm:text-sm text-gray-600">
            The transaction you're looking for doesn't exist or you don't have access to it.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => navigate('/user-transaction-history')}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Back to Transaction History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-5 sm:mb-6" aria-label="Breadcrumb">
        <ol role="list" className="flex items-center space-x-4">
          <li>
            <div>
              <button onClick={() => navigate('/user-dashboard')} className="text-gray-400 hover:text-gray-500">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
                </svg>
                <span className="sr-only">{t('userTransactionDetail.breadcrumb.home', 'Home')}</span>
              </button>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <button
                onClick={() => navigate('/user-transaction-history')}
                className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Transaction History
              </button>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <span className="ml-4 text-sm font-medium text-gray-900" aria-current="page">
                Transaction Details
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Transaction Details</h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">
          View detailed information about this transaction
        </p>
      </div>

      {/* Transaction Details Card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Transaction {transaction.transactionId}
              </h2>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(transaction.status)}`}>
                  {transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {formatAmount(transaction.amount)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {formatDate(transaction.date)}
              </div>
            </div>
          </div>

          {/* Transaction Information Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Transaction ID</label>
                <div className="text-base text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200">
                  {transaction.transactionId}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Amount</label>
                <div className="text-base text-gray-900">
                  {formatAmount(transaction.amount)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                <div className="text-base text-gray-900">
                  {transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Type</label>
                <div className="text-base text-gray-900">
                  {transaction.type}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Date</label>
                <div className="text-base text-gray-900">
                  {formatDate(transaction.date)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Time</label>
                <div className="text-base text-gray-900">
                  {formatTime(transaction.date)}
                </div>
              </div>

              {transaction.stripeId && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Stripe ID</label>
                  <div className="text-base text-gray-900 font-mono">
                    {transaction.stripeId}
                  </div>
                </div>
              )}

              {transaction.projectId && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Project ID</label>
                  <div className="text-base text-gray-900">
                    {transaction.projectId}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Details */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div className="space-y-3">
              {transaction.creditPackageId && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credit Package ID</span>
                  <span className="text-sm text-gray-900">{transaction.creditPackageId}</span>
                </div>
              )}

              {transaction.performedByUserId && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Performed By User ID</span>
                  <span className="text-sm text-gray-900">{transaction.performedByUserId}</span>
                </div>
              )}

              {transaction.refundAmount && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Refund Amount</span>
                  <span className="text-sm text-gray-900">{formatAmount(transaction.refundAmount)}</span>
                </div>
              )}

              {transaction.refundStatus && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Refund Status</span>
                  <span className="text-sm text-gray-900">{transaction.refundStatus}</span>
                </div>
              )}

              {transaction.refundStripeId && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Refund Stripe ID</span>
                  <span className="text-sm text-gray-900 font-mono">{transaction.refundStripeId}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error State (when external service unavailable) */}
      {showErrorState && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Service Temporarily Unavailable</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <Button
          onClick={() => navigate('/user-transaction-history')}
          variant="outline"
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 text-base sm:text-sm font-medium"
        >
          Back to History
        </Button>
        <Button
          onClick={openStripeReceipt}
          disabled={receiptLoading}
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base sm:text-sm font-medium transition-colors disabled:opacity-50"
        >
          {receiptLoading
            ? t('userTransactionDetail.receipt.loading', 'Loading…')
            : t('userTransactionDetail.receipt.open', 'View receipt on Stripe')}
        </Button>
      </div>
    </div>
  );
}

export default UserTransactionDetailPage;
